import { smoothScrollToElement } from '../../../../../util/util';
import { Card } from '../../../../../components/Card/Card';
import { Event } from '../../../../../model/event';
import { FormMultiSelect } from '../../../../../components/Form/Select/FormMultiSelect';
import { GroupedSeats } from '../../../../../components/Seatmap/GroupedSeats';
import React from 'react';
import { Seat } from '../../../../../model/seatmap/types';
import { Stack } from '../../../../../components/Stack/Stack';
import { compact } from 'lodash';
import { loadPricingCategories } from '../../../../../model/price';
import { printSeatedTickets } from '../../../../../model/ticket';
import { t } from '../../../../../locales/translate';
import { useAsync } from '../../../../../model/data';
import { useForm } from '@nerdgeschoss/react-use-form-library';
import { FormForm } from '../../../../../components/Form/FormForm';
import { FormSelect } from '../../../../../components/Form/Select/FormSelect';
import { ToggleField } from '../../../../../components/Form/toggle_field/toggle_field';
import { AnimatedHeight } from '../../../../../components/AnimatedHeight/AnimatedHeight';
import { FormTextArea } from '../../../../../components/Form/TextArea/FormTextArea';
import { FormTextField } from '../../../../../components/Form/TextField/FormTextField';
import { EmailTemplateField } from '../../../../../components/Form/email_template_field/email_template_field';

interface Props {
  event: Event;
  seats: Seat[];
  onTicketsCreated: (eventInfo: { seatIds: Array<string>; productVariantId: string; comment: string }) => Promise<void>;
  onClose: () => void;
}

interface Params {
  productVariantId: string | null;
  comment: string | null;
  tags?: string[];
  email: string;
  name: string;
  lastName?: string;
  phone?: string;
  sendViaEmail?: boolean;
  personalIdentifier?: string;
}

export function PrintSeatTicketOverlay({ seats, event, onClose, onTicketsCreated }: Props): JSX.Element {
  const [loading] = useAsync(async () => loadPricingCategories({ event, local: true }));

  const { model, fields, valid, dirty, onSubmit, submissionStatus } = useForm<Partial<Params>>({
    model: {
      productVariantId: null,
    },
    validations: {
      productVariantId: 'required',
      comment: 'required',
      email: [
        ({ model }) => {
          if (model.sendViaEmail && !model.email) {
            return ['required'];
          }
          return [];
        },
        'email',
      ],
      name: ({ model }) => {
        if (model.sendViaEmail && !model.name) {
          return ['required'];
        }
        return [];
      },
    },
    onSubmit: async (form) => {
      const { productVariantId, comment } = form.model;

      if (productVariantId && comment) {
        const tickets = {
          seatIds: seats.map((e) => e.id),
          productVariantId,
          comment,
          email: form.model.email,
          tags: form.model.tags,
          name: form.model.name,
          lastName: form.model.lastName,
          personalIdentifier: form.model.personalIdentifier,
        };
        await printSeatedTickets(tickets);
        await onTicketsCreated(tickets);
        smoothScrollToElement('print-history');
        onClose();
      }
    },
  });

  const pricingCategoryIds = new Set(
    seats.map((e) => e.pricingCategory?.id).filter((e) => e !== null && e !== undefined)
  );

  const variants = event.pricingCategories?.find((e) => pricingCategoryIds.has(e.id))?.productVariants ?? [];

  const canSubmit = [dirty, valid].every(Boolean);

  if (!loading && model.productVariantId === null && variants.length > 0) {
    if (variants.length > 0) {
      fields.productVariantId.onChange(variants[0].id);
    } else {
      // eslint-disable-next-line no-console
      console.warn('No product variants found for pricing category', {
        event,
        seats,
      });
    }
  }

  const tags = Array.from(new Set(compact(event.printedTickets?.flatMap((a) => a.order?.tags)).sort())) || [];

  if (pricingCategoryIds.size !== 1) return <></>;

  return (
    <Card size="xs" title={t.model.ticket.selectedTickets} actions={<span className="semibold">({seats.length})</span>}>
      <FormForm
        onSubmit={onSubmit}
        onClose={onClose}
        canSubmit={canSubmit}
        loading={loading || submissionStatus === 'submitting'}
      >
        <Stack>
          <Stack size="xs">
            <GroupedSeats seats={seats} />
            <FormSelect
              {...fields.productVariantId}
              disabled={loading}
              options={variants.map((e) => ({ value: e.id, label: e.description }))}
              label={t.model.price.priceVariant}
              loading={loading}
              required
            />
            <FormMultiSelect
              {...fields.tags}
              label={t.model.ticket.logoTags}
              options={tags.map((tag) => ({
                id: tag,
                label: tag,
              }))}
              value={model.tags}
              input
              description={t.page.event.ticket.print_section_ticket_overlay.tags_description}
            />
          </Stack>
          <FormTextField
            {...fields.personalIdentifier}
            label={t.model.ticket.personalIdentifier}
            description={t.model.ticket.personalIdentifierDescription}
          />
          <ToggleField
            label={t.page.event.ticket.print_section_ticket_overlay.send_via_email}
            value={!!fields.sendViaEmail.value}
            onChange={(v) => fields.sendViaEmail.onChange(v)}
          />
          <AnimatedHeight>
            {!model.sendViaEmail ? (
              <FormTextArea {...fields.comment} label={t.model.comment._name} />
            ) : (
              <Stack>
                <Stack size="xs">
                  <FormTextField {...fields.name} label={t.model.user.name} required />
                  <FormTextField {...fields.lastName} label={t.model.user.lastName} />
                  <FormTextField {...fields.email} type="email" label={t.model.user.email} required />
                  <FormTextField {...fields.phone} type="number" label={t.model.user.phone} />
                  <FormTextArea
                    {...fields.comment}
                    label={t.page.event.ticket.print_section_ticket_overlay.email_text}
                  />
                </Stack>
                <EmailTemplateField
                  eventId={event.id}
                  data={{
                    firstName: model.name || null,
                    lastName: model.lastName || null,
                    email: model.email || null,
                    phone: model.phone || null,
                    ticketsCount: seats.length,
                    text: model.comment || null,
                  }}
                />
              </Stack>
            )}
          </AnimatedHeight>
        </Stack>
      </FormForm>
    </Card>
  );
}
